import { useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { MediaPickerModal } from "@/components/media/MediaPickerModal";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { SafeImage } from "@/components/ui/SafeImage";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { useCreatePartner, useDeletePartner, usePartners, useUpdatePartner } from "@/features/partners/hooks";
import type { Partner, PartnerInput } from "@/features/partners/types";

const emptyForm: PartnerInput = {
  name: "",
  slug: "",
  websiteUrl: "",
  logoKey: null,
  blurb: "",
  featured: false,
  sortOrder: 0,
  isActive: true,
};

export function PartnersPage() {
  const currentUser = useCurrentUser();
  const partners = usePartners();
  const createPartner = useCreatePartner();
  const updatePartner = useUpdatePartner();
  const deletePartner = useDeletePartner();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Partner | null>(null);
  const [form, setForm] = useState<PartnerInput>(emptyForm);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");

  const sortedPartners = useMemo(
    () => [...(partners.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [partners.data],
  );

  function openCreate() {
    setSelectedPartner(null);
    setForm(emptyForm);
    setError(null);
    setDrawerMode("create");
  }

  function openEdit(partner: Partner) {
    setSelectedPartner(partner);
    setForm({
      name: partner.name,
      slug: partner.slug,
      websiteUrl: partner.websiteUrl ?? "",
      logoKey: partner.logoKey,
      blurb: partner.blurb ?? "",
      featured: partner.featured,
      sortOrder: partner.sortOrder,
      isActive: partner.isActive,
    });
    setError(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedPartner(null);
    setForm(emptyForm);
    setError(null);
  }

  function isValidOptionalUrl(value: string | null | undefined) {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!isValidOptionalUrl(form.websiteUrl)) {
      setError("Website URL must be a valid http or https URL.");
      return;
    }

    if (drawerMode === "create") {
      createPartner.mutate(
        { input: form, logoFile: null },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to create partner."),
        },
      );
      return;
    }

    if (selectedPartner) {
      updatePartner.mutate(
        { partner: selectedPartner, input: form, logoFile: null },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to update partner."),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deletePartner.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Partners"
        description="Manage active and featured partners shown by the public partner endpoint."
        actions={<Button onClick={openCreate}>New Partner</Button>}
      />
      <AdminDataTable
        rows={sortedPartners}
        getRowKey={(partner) => partner.id}
        emptyState={
          <EmptyState
            title={partners.isLoading ? "Loading partners..." : "No partners yet"}
            description="Create the first partner to start building the partner list."
            action={!partners.isLoading && <Button onClick={openCreate}>Create Partner</Button>}
          />
        }
        columns={[
          {
            key: "logo",
            header: "Logo",
            render: (partner) =>
              partner.logoUrl ? (
                <SafeImage src={partner.logoUrl} alt={`${partner.name} logo`} fallbackLabel={partner.name.slice(0, 2)} className="size-14 rounded-md object-cover" />
              ) : (
                <div className="grid size-14 place-items-center rounded-md border border-cyan-300/15 bg-black/40 text-xs text-content-muted">None</div>
              ),
          },
          {
            key: "name",
            header: "Name",
            render: (partner) => (
              <div>
                <p className="font-medium text-white">{partner.name}</p>
                <p className="mt-1 text-xs text-content-muted">/{partner.slug}</p>
              </div>
            ),
          },
          {
            key: "website",
            header: "Website",
            render: (partner) =>
              partner.websiteUrl ? (
                <a href={partner.websiteUrl} target="_blank" rel="noreferrer" className="text-cyan-100 hover:text-cyan-300">
                  {partner.websiteUrl}
                </a>
              ) : (
                <span className="text-content-muted">No website</span>
              ),
          },
          { key: "sortOrder", header: "Sort", render: (partner) => partner.sortOrder },
          {
            key: "status",
            header: "Status",
            render: (partner) => (
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={partner.isActive ? "active" : "inactive"}>{partner.isActive ? "Active" : "Inactive"}</StatusBadge>
                {partner.featured && <StatusBadge tone="warning">Featured</StatusBadge>}
              </div>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            className: "px-4 py-4 align-middle text-right",
            render: (partner) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(partner)}>
                  Edit
                </Button>
                {canDelete && partner.deletedAt === null && (
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(partner)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <AdminFormDrawer
        isOpen={drawerMode !== null}
        title={drawerMode === "create" ? "New Partner" : "Edit Partner"}
        description="Choose an image from the centralized Media Library."
        onClose={closeDrawer}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Name" value={form.name} onChange={(value) => setForm((current) => ({ ...current, name: value }))} required />
          <TextField label="Slug" value={form.slug ?? ""} onChange={(value) => setForm((current) => ({ ...current, slug: value }))} />
          <TextField label="Website URL" value={form.websiteUrl ?? ""} type="url" onChange={(value) => setForm((current) => ({ ...current, websiteUrl: value }))} />
          <label className="block">
            <span className="text-sm text-content-secondary">Description</span>
            <textarea
              className="mt-2 min-h-28 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
              value={form.blurb ?? ""}
              onChange={(event) => setForm((current) => ({ ...current, blurb: event.target.value }))}
              maxLength={500}
            />
          </label>
          <div className="rounded-md border border-cyan-300/15 bg-black/20 p-3">
            <p className="text-sm text-content-secondary">Logo / Image</p>
            {form.logoKey ? <p className="mt-2 truncate text-xs text-cyan-100">{form.logoKey}</p> : <p className="mt-2 text-xs text-content-muted">No media selected</p>}
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setMediaPickerOpen(true)}>Choose Media</Button>
              {form.logoKey && <Button type="button" variant="ghost" onClick={() => setForm((current) => ({ ...current, logoKey: null }))}>Clear</Button>}
            </div>
          </div>
          <TextField
            label="Sort Order"
            value={String(form.sortOrder ?? 0)}
            type="number"
            onChange={(value) => setForm((current) => ({ ...current, sortOrder: Number(value) }))}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <ToggleField label="Active" checked={Boolean(form.isActive)} onChange={(value) => setForm((current) => ({ ...current, isActive: value }))} />
            <ToggleField label="Featured" checked={Boolean(form.featured)} onChange={(value) => setForm((current) => ({ ...current, featured: value }))} />
          </div>
          {!canDelete && <p className="text-xs text-content-muted">Only Admin, Owner, and Developer roles can delete partners.</p>}
          {error && <p className="rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" disabled={createPartner.isPending || updatePartner.isPending}>
              {createPartner.isPending || updatePartner.isPending ? "Saving..." : "Save Partner"}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      <MediaPickerModal
        isOpen={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={(items) => setForm((current) => ({ ...current, logoKey: items[0]?.filename ?? current.logoKey }))}
      />

      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Partner"
        description={`Soft delete "${deleteTarget?.name ?? "this partner"}"?`}
        isPending={deletePartner.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url" | "number";
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
      />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
      <input className="size-4 accent-cyan-300" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm text-content-secondary">{label}</span>
    </label>
  );
}
